import { Component, EventEmitter, Input, OnInit, OnChanges, Output, ChangeDetectionStrategy, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule, FormControl, AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Util } from '@shared/common/utils/util';
import { StorageService } from '@services/storage/storage.service';
import { IonCheckbox, IonIcon } from "@ionic/angular/standalone";
import { CurrencyFormatDirective } from '@shared/directives/currency-format.directive';
import { TipoMensajeSalidaFlujoTramitePension } from '@constants/business.constant';
import { ModalService } from '@services/modal/modal.service';

const MIN_CUOTAS_DESTINO = 0.01;
const SALDO_CUOTAS_SIN_EDICION = 1;

@Component({
  selector: 'app-preguntas-ingreso-extra',
  templateUrl: './preguntas-ingreso-extra.component.html',
  styleUrls: ['./preguntas-ingreso-extra.component.scss'],
  standalone: true,
  imports: [IonIcon, IonCheckbox, CommonModule, ReactiveFormsModule, CurrencyFormatDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreguntasIngresoExtraComponent implements OnInit, OnChanges {

  @Input() form!: FormGroup;
  @Input() question!: any;
  @Input() index = 0;
  @Input() total = 0;
  @Input() isActive = false;
  @Input() resumenRefreshToken = 0;
  @Output() saved = new EventEmitter<void>();

  mode: 'summary' | 'edit' = 'summary';
  private readonly storageKey = 'crearSaldoInternoTramitePension';
  accountBalance: any = null;
  fondosConCategorias: any[] = [];
  categoriasDesglosadaPorRegimen: any[] = [];
  resumenSaldoInterno: any[] = [];
  resumenAgrupadoPorTipoCuenta: any[] = [];
  hasSavedAmounts = false;
  producto: string = "";
  /** Errores de monto por controlName. */
  amountErrors: Record<string, string | null> = {};
  includeAllByControl: Record<string, boolean> = {};
  private includeAllGuardUntil: Record<string, number> = {};

  constructor(private readonly util: Util, private readonly storageService: StorageService, private readonly cdr: ChangeDetectorRef,
    private readonly modalService: ModalService
  ) { }

  async ngOnInit() {
    this.mode = 'summary';
    this.initializeAccountBalance();
    await this.loadResumenSaldoInterno();
    this.setupModeByRadio();
  }

  ngOnChanges(changes: SimpleChanges) {
    const shouldRecomputeMode = Boolean(changes['isActive'] || changes['question'] || changes['form']);

    if (shouldRecomputeMode) {
      const radioControl = this.form?.get(this.question?.controlName);
      const selectedValue = radioControl?.value;
      this.mode = this.resolveMode(selectedValue, false);
    }    
    // Rehidratar desde sesión cuando cambian inputs clave (ej: volver de otra página).
    if (shouldRecomputeMode) {
      void this.loadResumenSaldoInterno();
    }

    if (changes['resumenRefreshToken']) {
      void this.loadResumenSaldoInterno();
    }

    this.cdr.markForCheck();
  }

  get isLastQuestion(): boolean {
    return this.total > 0 && this.index === this.total;
  }

  private setupModeByRadio(): void {
    const radioControl = this.form?.get(this.question?.controlName);
    if (!radioControl) return;

    const isNoValue = (value: unknown): boolean => {
      return value === false || value === 'false' || value === 'NO' || value === 'No';
    };

    const updateMode = async (selectedValue: unknown, isFromUserChange: boolean): Promise<void> => {
      const shouldAdvanceOnNo = isFromUserChange && isNoValue(selectedValue);
      const isYesValue =
        selectedValue === true ||
        selectedValue === 'true' ||
        selectedValue === 'SI' ||
        selectedValue === 'Sí';

      // Garantiza la persistencia del aporte ESTADO en CCV apenas se responde Sí/No.
      /*if (isAnswered && isFromUserChange) {
        await this.ensureCcvEstadoDefaultInSession();
      }*/

      this.mode = this.resolveMode(selectedValue, isFromUserChange);

      if (this.mode === 'edit') {
        this.applyAutoIncludeAllForLowShareBalances();
      }

      const shouldAutoIncludeAllAndSave =
        isYesValue &&
        this.todosLosFondosEditablesTienenSaldoCuotasMenorAUno() &&
        (isFromUserChange || !this.hasSavedDataForCurrentProduct());

      if (shouldAutoIncludeAllAndSave) {
        await this.autoIncludeAllLowShareBalancesAndSave();
        this.cdr.markForCheck();
        return;
      }

      if (!this.isActive) {
        // En cambios de usuario, permitir avance por NO aunque el foco activo se actualice después.
        if (shouldAdvanceOnNo) {
          await this.clearSessionDataForCurrentProduct();
          this.saved.emit();
        }
        this.cdr.markForCheck();
        return;
      }

      // Avanzar solo cuando el usuario selecciona No explícitamente.
      if (shouldAdvanceOnNo) {
        await this.clearSessionDataForCurrentProduct();
        this.saved.emit();
      }

      this.cdr.markForCheck();
    };

    void updateMode(radioControl.value, false);
    radioControl.valueChanges.subscribe((value) => {
      void updateMode(value, true);
    });
  }

  private resolveMode(selectedValue: unknown, isFromUserChange: boolean): 'summary' | 'edit' {
    const isYesValue = selectedValue === true || selectedValue === 'true' || selectedValue === 'SI' || selectedValue === 'Sí';

    if (!this.isActive) {
      return 'summary';
    }

    if (!isYesValue) {
      return 'summary';
    }

    if (this.todosLosFondosEditablesTienenSaldoCuotasMenorAUno()) {
      return 'summary';
    }

    if (isFromUserChange) {
      return 'edit';
    }

    // Al volver al flujo, si ya hay montos guardados, mostrar resumen en vez de abrir edición.
    return this.hasSavedDataForCurrentProduct() ? 'summary' : 'edit';
  }

  private async clearSessionDataForCurrentProduct(): Promise<void> {
    const tipoProducto = this.accountBalance?.id || this.question?.type;
    if (!tipoProducto) return;

    const registrosStorage = await this.storageService.get(this.storageKey);
    const registrosExistentes = Array.isArray(registrosStorage) ? registrosStorage : [];
    const registrosOtrosProductos = registrosExistentes.filter(
      (item: any) => item?.tipoProducto !== tipoProducto
    );

    const registrosFiltrados = [...registrosOtrosProductos];

    // Para CCV, mantener siempre el aporte ESTADO aunque el usuario seleccione "No".
    /*if (tipoProducto === 'CCV' || tipoProducto === 'CVC') {
      this.appendCcvEstadoDefaultRecords(registrosFiltrados, tipoProducto);
    }*/

    await this.storageService.set(this.storageKey, registrosFiltrados);
    this.resumenSaldoInterno = registrosFiltrados;
    this.resumenAgrupadoPorTipoCuenta = this.buildResumenAgrupadoPorTipoCuenta();
    this.hasSavedAmounts = this.hasSavedDataForCurrentProduct();
    this.cdr.markForCheck();
  }

  private async loadResumenSaldoInterno(): Promise<void> {
    const registrosStorage = await this.storageService.get(this.storageKey);
    this.resumenSaldoInterno = Array.isArray(registrosStorage) ? registrosStorage : [];

    this.hydrateFormWithSavedAmountsForCurrentProduct();
    this.resumenAgrupadoPorTipoCuenta = this.buildResumenAgrupadoPorTipoCuenta();
    this.hasSavedAmounts = this.hasSavedDataForCurrentProduct();
    this.cdr.markForCheck();
  }

  private async ensureCcvEstadoDefaultInSession(): Promise<void> {
    const tipoProducto = this.accountBalance?.id || this.question?.type;
    if (tipoProducto !== 'CCV' || tipoProducto !== 'CVC') {
      return;
    }

    const registrosStorage = await this.storageService.get(this.storageKey);
    const registrosExistentes = Array.isArray(registrosStorage) ? registrosStorage : [];
    const registrosOtrosProductos = registrosExistentes.filter(
      (item: any) => item?.tipoProducto !== tipoProducto
    );
    const registrosProductoActual = registrosExistentes.filter(
      (item: any) => item?.tipoProducto === tipoProducto
    );

    const totalAntes = registrosProductoActual.length;
    //this.appendCcvEstadoDefaultRecords(registrosProductoActual, tipoProducto);

    if (registrosProductoActual.length !== totalAntes) {
      await this.storageService.set(this.storageKey, [...registrosOtrosProductos, ...registrosProductoActual]);
    }
  }

  private hydrateFormWithSavedAmountsForCurrentProduct(): void {
    const tipoProducto = this.accountBalance?.id || this.question?.type;
    if (!tipoProducto || !this.form) {
      return;
    }

    const registrosProductoActual = this.resumenSaldoInterno.filter(
      (item: any) => item?.tipoProducto === tipoProducto
    );

    registrosProductoActual.forEach((registro: any) => {
      const controlName = this.getCategoryControlName(
        registro.tipoFondo,
        registro.categoria,
        registro.codigoRegimenTributario
      );

      const control = this.form.get(controlName);
      if (!control) {
        return;
      }

      const savedAmount = this.getClpBalanceValue(registro.valorMontoSolicitado ?? 0);
      control.setValue(Number.isNaN(savedAmount) ? 0 : savedAmount, { emitEvent: false });
    });
  }

  private buildResumenAgrupadoPorTipoCuenta(): any[] {
    const grupos = new Map<string, any>();

    this.resumenSaldoInterno.forEach((registro: any) => {
      const tipoCuenta = registro?.glosaTipoCuenta || 'Sin tipo de cuenta';

      if (!grupos.has(tipoCuenta)) {
        grupos.set(tipoCuenta, {
          tipoCuenta,
          filas: []
        });
      }

      grupos.get(tipoCuenta).filas.push(registro);
    });

    return Array.from(grupos.values());
  }

  private hasSavedDataForCurrentProduct(): boolean {
    const tipoProducto = this.accountBalance?.id || this.question?.type;
    if (!tipoProducto) return false;

    const registrosProducto = this.resumenSaldoInterno.filter(
      (item: any) => item?.tipoProducto === tipoProducto
    );

    // Para CCV, un registro solo de ESTADO es dato por defecto y no habilita vista/edit de montos editados.
    if (tipoProducto === 'CCV' || tipoProducto === 'CVC') {
      return registrosProducto.some((item: any) => item?.categoria !== 'ESTADO');
    }

    return registrosProducto.length > 0;
  }

  get canShowEditButton(): boolean {
    return (
      this.mode === 'summary' &&
      this.hasSavedAmounts &&
      !this.todosLosFondosEditablesTienenSaldoCuotasMenorAUno()
    );
  }

  get summaryAmountHeader(): string {
    return this.hasSavedAmounts ? 'Monto destinado*' : 'Saldo en pesos*';
  }

  openEditFromSummary(): void {
    if (this.todosLosFondosEditablesTienenSaldoCuotasMenorAUno()) {
      return;
    }
    this.hydrateFormWithSavedAmountsForCurrentProduct();
    this.applyAutoIncludeAllForLowShareBalances();
    this.mode = 'edit';
    this.cdr.markForCheck();
  }

  getSummaryAmountValue(fondo: any, categoria: string): string {
    const tipoProducto = this.accountBalance?.id || this.question?.type;
    const registroGuardado = this.resumenSaldoInterno.find((item: any) =>
      item?.tipoProducto === tipoProducto &&
      item?.tipoFondo === fondo.id &&
      item?.categoria === categoria &&
      item?.codigoRegimenTributario === fondo.taxRegimeCode
    );

    if (!registroGuardado) {
      return String(fondo.clpBalance ?? '0');
    }

    return this.formatMontoResumen(registroGuardado.valorMontoSolicitado);
  }

  get filasResumenAgrupadasPorRegimen(): any[] {
    const grupos = new Map<string, any>();

    this.fondosConCategorias.forEach((categoria) => {
      categoria.fondos.forEach((fondo: any) => {
        const regimen = fondo?.glosaCodigoRegimen || 'Sin régimen';

        if (!grupos.has(regimen)) {
          grupos.set(regimen, {
            regimen,
            filas: []
          });
        }

        grupos.get(regimen).filas.push({
          fondo,
          categoria: categoria.category
        });
      });
    });

    return Array.from(grupos.values());
  }

  getCategoriaGlosaCodigoRegimen(categoria: any): string {
    if (!categoria?.fondos || !Array.isArray(categoria.fondos)) {
      return categoria?.category || '';
    }

    const glosasUnicas = Array.from(
      new Set(
        categoria.fondos
          .map((fondo: any) => fondo?.glosaCodigoRegimen)
          .filter((glosa: string | undefined) => !!glosa)
      )
    ) as string[];

    if (glosasUnicas.length === 0) {
      return categoria?.category || '';
    }

    return glosasUnicas.join(' / ');
  }

  private isCcvEstadoNoEditable(categoria: string): boolean {
    const tipoProducto = this.accountBalance?.id || this.question?.type;
    return (tipoProducto === 'CCV' || tipoProducto === 'CVC') && categoria === 'ESTADO';
  }

  todosLosFondosEditablesTienenSaldoCuotasMenorAUno(): boolean {
    const fondos = this.getFondosEditables();
    if (fondos.length === 0) {
      return false;
    }
    return fondos.every((fondo) => this.getShareBalanceValue(fondo) < SALDO_CUOTAS_SIN_EDICION);
  }

  private getFondosEditables(): any[] {
    const fondos: any[] = [];
    this.categoriasDesglosadaPorRegimen.forEach((categoria) => {
      categoria.regimenes?.forEach((regimen: any) => {
        regimen.fondos?.forEach((fondo: any) => {
          fondos.push(fondo);
        });
      });
    });
    return fondos;
  }

  private applyAutoIncludeAllForLowShareBalances(): void {
    this.categoriasDesglosadaPorRegimen.forEach((categoria) => {
      categoria.regimenes?.forEach((regimen: any) => {
        regimen.fondos?.forEach((fondo: any) => {
          const shareBalance = this.getShareBalanceValue(fondo);
          if (shareBalance <= 0 || shareBalance >= SALDO_CUOTAS_SIN_EDICION) {
            return;
          }

          const control = this.form?.get(fondo.controlName);
          if (!control) {
            return;
          }

          this.includeAllByControl[fondo.controlName] = true;
          this.reapplyIncludeAllAmount(fondo, categoria.category, fondo.controlName, control);
        });
      });
    });
  }

  private async autoIncludeAllLowShareBalancesAndSave(): Promise<void> {
    this.categoriasDesglosadaPorRegimen.forEach((categoria) => {
      categoria.regimenes?.forEach((regimen: any) => {
        regimen.fondos?.forEach((fondo: any) => {
          const control = this.form?.get(fondo.controlName);
          if (!control) {
            return;
          }
          this.includeAllByControl[fondo.controlName] = true;
          this.reapplyIncludeAllAmount(fondo, categoria.category, fondo.controlName, control);
        });
      });
    });
    await this.save();
  }

  get categoriasParaEdicion(): any[] {
    return this.fondosConCategorias.filter(
      (categoria) => !this.isCcvEstadoNoEditable(categoria.category)
    );
  }

  private buildCategoriasDesglosadaPorRegimen(): any[] {
    const result: any[] = [];

    this.categoriasParaEdicion.forEach((categoria) => {
      const regimenesPorCategoria = new Map<string, any>();

      categoria.fondos.forEach((fondo: any) => {
        const taxRegimeCode = fondo.taxRegimeCode || 'DEFAULT';
        const controlName = this.getCategoryControlName(fondo.id, categoria.category, fondo.taxRegimeCode);

        if (!regimenesPorCategoria.has(taxRegimeCode)) {
          regimenesPorCategoria.set(taxRegimeCode, {
            taxRegimeCode,
            glosaCodigoRegimen: fondo.glosaCodigoRegimen,
            fondos: []
          });
        }

        regimenesPorCategoria.get(taxRegimeCode).fondos.push({
          ...fondo,
          controlName
        });
      });

      const regimenes = Array.from(regimenesPorCategoria.values());

      result.push({
        category: categoria.category,
        regimenes
      });
    });

    return result;
  }

  enforceMaxAmount(event: Event, fondo: any, categoria: string): void {
    // Mantener compatibilidad del template: ahora valida min/max sin recortar.
    this.onAmountBlur(event, fondo, categoria);
  }

  onAmountBlur(event: Event, fondo: any, categoria: string): void {
    const controlName = this.getCategoryControlName(fondo.id, categoria, fondo.taxRegimeCode);
    const control = this.form.get(controlName);
    if (!control) return;

    setTimeout(() => {
      if (this.shouldProtectIncludeAll(controlName)) {
        this.reapplyIncludeAllAmount(fondo, categoria, controlName, control);
        return;
      }

      const input = event.target as HTMLInputElement | null;
      const rawValue = input?.value ?? String(control.value ?? '');
      const currentValue = this.getClpBalanceValue(rawValue);
      const numericValue = Number.isNaN(currentValue) ? null : currentValue;

      if (numericValue !== null && this.includeAllByControl[controlName]) {
        const maxAmount = this.getMaxAmountPesos(fondo);
        if (numericValue !== maxAmount) {
          this.includeAllByControl[controlName] = false;
        }
      }

      control.setValue(numericValue, { emitEvent: false });
      this.validateFondoAmount(fondo, categoria);
      this.cdr.markForCheck();
    }, 0);
  }

  onAmountInput(event: Event, fondo: any, categoria: string): void {
    const controlName = this.getCategoryControlName(fondo.id, categoria, fondo.taxRegimeCode);
    if (this.includeAllByControl[controlName]) {
      this.includeAllByControl[controlName] = false;
    }

    const input = event.target as HTMLInputElement | null;
    const digitsOnly = String(input?.value ?? '').replaceAll(/\D/g, '');
    const control = this.form.get(controlName);

    if (!digitsOnly) {
      control?.setValue(null, { emitEvent: false });
      this.amountErrors[controlName] = null;
      this.cdr.markForCheck();
      return;
    }

    const currentValue = Number.parseInt(digitsOnly, 10);
    if (control && !Number.isNaN(currentValue)) {
      // setValue vuelve a escribir el número crudo en el input; reaplicamos el formato visual.
      control.setValue(currentValue, { emitEvent: false });
      if (input) {
        const formatted = this.formatInputCurrency(currentValue);
        input.value = formatted;
        const cursorPos = formatted.length;
        input.setSelectionRange(cursorPos, cursorPos);
      }
    }

    this.validateFondoAmount(fondo, categoria);
    this.cdr.markForCheck();
  }

  getMaxAmountPesos(fondo: any): number {
    const maxAmount = this.getClpBalanceValue(fondo.clpBalanceRaw ?? fondo.clpBalance ?? 0);
    return Number.isNaN(maxAmount) ? 0 : maxAmount;
  }

  /**
   * Equivalente en pesos de 0.01 cuotas (redondeo hacia arriba a peso entero).
   */
  getMinAmountPesos(fondo: any): number {
    const shareValue = this.getShareValue(fondo);
    if (shareValue <= 0) {
      return 0;
    }
    return Math.max(1, Math.ceil(MIN_CUOTAS_DESTINO * shareValue));
  }

  getShareBalanceValue(fondo: any): number {
    return this.parseDecimalValue(fondo?.shareBalance);
  }

  formatMontoAyuda(valor: number): string {
    return `$${this.util.currencyFormat(valor)}`;
  }

  getTextoSaldoMinimoRegimen(regimen: any): string {
    const primerFondo = regimen?.fondos?.[0];
    if (!primerFondo) {
      return 'Puedes editar el monto en pesos que destinarás a la pensión.';
    }
    const minPesos = this.getMinAmountPesos(primerFondo);
    return `Puedes editar el monto en pesos que destinarás a la pensión. El saldo mínimo para agregar a la pensión son ${this.formatMontoAyuda(minPesos)}.`;
  }

  getAmountError(controlName: string): string | null {
    return this.amountErrors[controlName] ?? null;
  }

  hasActiveAmountErrors(): boolean {
    return Object.values(this.amountErrors).some(Boolean);
  }

  isIncludeAll(controlName: string): boolean {
    return Boolean(this.includeAllByControl[controlName]);
  }

  onIncludeAllChange(fondo: any, categoria: string, checked: boolean): void {
    const controlName = this.getCategoryControlName(fondo.id, categoria, fondo.taxRegimeCode);
    const control = this.form.get(controlName);
    if (!control) return;

    // Con [checked] one-way, Ionic a veces emite true y luego false en el mismo click.
    if (!checked && Date.now() < (this.includeAllGuardUntil[controlName] ?? 0)) {
      this.includeAllByControl[controlName] = true;
      this.reapplyIncludeAllAmount(fondo, categoria, controlName, control);
      return;
    }

    this.includeAllByControl[controlName] = checked;

    if (checked) {
      this.includeAllGuardUntil[controlName] = Date.now() + 400;
      this.reapplyIncludeAllAmount(fondo, categoria, controlName, control);

      // Reaplicar después del blur/microtask de la directiva currency.
      queueMicrotask(() => {
        if (this.shouldProtectIncludeAll(controlName)) {
          this.reapplyIncludeAllAmount(fondo, categoria, controlName, control);
        }
      });
      setTimeout(() => {
        if (this.shouldProtectIncludeAll(controlName)) {
          this.includeAllByControl[controlName] = true;
          this.reapplyIncludeAllAmount(fondo, categoria, controlName, control);
        }
      }, 0);
      return;
    }

    this.validateFondoAmount(fondo, categoria);
    this.cdr.detectChanges();
  }

  /** Evita que el input pierda el foco al pulsar el checkbox (dispara el blur que pisa el monto). */
  onIncludeAllPointerDown(event: Event): void {
    event.preventDefault();
  }

  private shouldProtectIncludeAll(controlName: string): boolean {
    return (
      this.includeAllByControl[controlName] === true ||
      Date.now() < (this.includeAllGuardUntil[controlName] ?? 0)
    );
  }

  private reapplyIncludeAllAmount(
    fondo: any,
    categoria: string,
    controlName: string,
    control: AbstractControl
  ): void {
    const maxAmount = this.getMaxAmountPesos(fondo);
    control.setValue(maxAmount, { emitEvent: false });
    this.syncAmountInputsDisplay(controlName, maxAmount);
    this.amountErrors[controlName] = null;
    this.validateFondoAmount(fondo, categoria);
    this.cdr.detectChanges();
  }

  private syncAmountInputsDisplay(controlName: string, amount: number): void {
    const formatted = this.formatInputCurrency(amount);
    const ids = [controlName, `${controlName}_mobile`];
    ids.forEach((id) => {
      const input = document.getElementById(id) as HTMLInputElement | null;
      if (input) {
        input.value = formatted;
      }
    });
  }

  private esSaldoPolvoConIncluirTodo(
    includeAll: boolean,
    shareBalance: number,
    maxAmount: number,
    minAmount: number
  ): boolean {
    if (!includeAll || shareBalance <= 0) {
      return false;
    }

    return (
      shareBalance <= MIN_CUOTAS_DESTINO ||
      (maxAmount > 0 && maxAmount < minAmount)
    );
  }

  validateFondoAmount(fondo: any, categoria: string): boolean {
    const controlName = this.getCategoryControlName(fondo.id, categoria, fondo.taxRegimeCode);
    const control = this.form.get(controlName);
    if (!control) {
      return true;
    }

    const maxAmount = this.getMaxAmountPesos(fondo);
    const minAmount = this.getMinAmountPesos(fondo);
    const shareBalance = this.getShareBalanceValue(fondo);
    const rawValue = control.value;
    const currentValue = this.getClpBalanceValue(rawValue ?? 0);
    const includeAll = this.isIncludeAll(controlName);

    if (this.esSaldoPolvoConIncluirTodo(includeAll, shareBalance, maxAmount, minAmount)) {
      this.amountErrors[controlName] = null;
      return true;
    }

    if (rawValue === null || rawValue === undefined || rawValue === '' || Number.isNaN(currentValue)) {
      this.amountErrors[controlName] = `Ingresa un monto mayor o igual a ${this.formatMontoAyuda(minAmount)}`;
      return false;
    }

    if (currentValue > maxAmount) {
      this.amountErrors[controlName] = `Ingresa un monto menor o igual a ${this.formatMontoAyuda(maxAmount)}`;
      return false;
    }

    if (currentValue < minAmount) {
      this.amountErrors[controlName] = `Ingresa un monto mayor o igual a ${this.formatMontoAyuda(minAmount)}`;
      return false;
    }

    this.amountErrors[controlName] = null;
    return true;
  }

  private validateAllEditableAmounts(): boolean {
    let isValid = true;

    this.categoriasDesglosadaPorRegimen.forEach((categoria) => {
      categoria.regimenes.forEach((regimen: any) => {
        regimen.fondos.forEach((fondo: any) => {
          if (!this.validateFondoAmount(fondo, categoria.category)) {
            isValid = false;
          }
        });
      });
    });

    return isValid;
  }

  private formatInputCurrency(value: number): string {
    if (!value || value === 0) {
      return '';
    }

    const formatted = new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);

    return `$ ${formatted}`;
  }

  formatMontoResumen(valor: string | number): string {
    const valorLimpio = this.getClpBalanceValue(valor ?? 0);
    return this.util.currencyFormat(Number.isNaN(valorLimpio) ? 0 : valorLimpio);
  }

  get totalResumen(): number {
    return this.resumenSaldoInterno.reduce((suma, registro) => {
      const valor = this.getClpBalanceValue(registro.valorMontoSolicitado ?? 0);
      return suma + (Number.isNaN(valor) ? 0 : valor);
    }, 0);
  }

  /**
   * Inicializar datos del balance si existen
   * Extrae los fondos con sus categorías desde accountBalance
   */
  private initializeAccountBalance(): void {
    if (this.question?.accountBalance) {
      this.accountBalance = this.question.accountBalance;
      this.fondosConCategorias = this.extractCategoriasFromFundos();
      this.categoriasDesglosadaPorRegimen = this.buildCategoriasDesglosadaPorRegimen();
      this.producto = this.util.nombreProductoSegunID(this.question.type) || "";

      // Crear dinámicamente los controles del formulario
      this.createDynamicControls();
    }
  }

  /**
   * Crea dinámicamente los controles del formulario basándose en la estructura de fondos y categorías
   * Esto evita errores de controles no encontrados en el FormGroup
   */
  private createDynamicControls(): void {
    this.categoriasDesglosadaPorRegimen.forEach((categoria) => {
      categoria.regimenes.forEach((regimen: any) => {
        regimen.fondos.forEach((fondo: any) => {
          const controlName = fondo.controlName;

          // Crear el control si no existe
          if (!this.form.get(controlName)) {
            const initialValue: number | null = null;
            this.form.addControl(controlName, new FormControl(initialValue));
          }
        });
      });
    });
  }

  /**
   * Genera un nombre único para el control de una categoría específica
   * Incluye taxRegimeCode para evitar duplicados cuando fondo y categoría son iguales
   * Si taxRegimeCode es vacío/null, asigna un número X basado en el índice
   * Ej: transferfundsCAV_C_RETIRO10_AFI o transferfundsCAV_C_RETIRO10_X0
   */
  getCategoryControlName(fondoId: string, categoria: string, taxRegimeCode: string | null | undefined): string {
    const regimen = taxRegimeCode?.trim() ? taxRegimeCode : 'sin_regimen';
    return `${this.question.controlName}_${fondoId}_${categoria}_${regimen}`.toLowerCase();
  }

  /**
   * Obtiene el valor formateado de clpBalance como número (sin formato)
   */
  getClpBalanceValue(clpBalance: string | number): number {
    if (typeof clpBalance === 'number') return clpBalance;
    const digitsOnly = String(clpBalance).replaceAll(/\D/g, '');
    if (!digitsOnly) {
      return Number.NaN;
    }

    return Number.parseInt(digitsOnly, 10);
  }

  /**
   * Obtiene el valor de shareValue de forma segura
   * Convierte correctamente strings con formato CLP (coma decimal) a número
   * Si no existe o es inválido, retorna 0
   */
  getShareValue(fondo: any): number {
    const shareValueRaw = fondo?.shareValue;
    const shareValue = this.parseDecimalValue(shareValueRaw);
    
    console.log('[PreguntasIngresoExtra] getShareValue - fondo.id:', fondo?.id, 'shareValue parseado:', shareValue, 'shareValue original:', shareValueRaw);
    
    if (Number.isNaN(shareValue) || !Number.isFinite(shareValue) || shareValue <= 0) {
      console.warn('[PreguntasIngresoExtra] shareValue inválido para fondo:', fondo?.id, 'valor recibido:', shareValueRaw);
      return 0;
    }
    return shareValue;
  }

  /**
   * Convierte un string con formato de decimal (coma o punto) a número
   * Maneja correctamente formato CLP: "78863,98" -> 78863.98
   * También soporta formato internacional: "78863.98" -> 78863.98
   */
  private parseDecimalValue(value: string | number | undefined): number {
    if (value === undefined || value === null) {
      return 0;
    }

    // Si ya es número, retornar directamente
    if (typeof value === 'number') {
      return value;
    }

    // Convertir a string y limpiar espacios
    let stringValue = String(value).trim();

    // Si no hay nada, retornar 0
    if (!stringValue) {
      return 0;
    }

    // Detectar y reemplazar coma decimal (formato chileno) por punto
    // Primero eliminar separadores de miles (si existen)
    // Formato chileno: 1.234.567,89 -> 1234567.89
    // Formato internacional: 1,234,567.89 -> 1234567.89
    
    // Contar comas y puntos para determinar formato
    const commaCount = (stringValue.match(/,/g) || []).length;
    const dotCount = (stringValue.match(/\./g) || []).length;

    if (commaCount === 1 && dotCount === 0) {
      // Formato: "78863,98" (coma es decimal)
      stringValue = stringValue.replace(',', '.');
    } else if (commaCount === 0 && dotCount === 1) {
      // Formato: "78863.98" (punto es decimal)
      // No hacer nada
    } else if (commaCount > 0 && dotCount > 0) {
      // Formato: "1.234.567,89" o "1,234,567.89"
      // Determinar cuál es el separador decimal (el último)
      const lastCommaIndex = stringValue.lastIndexOf(',');
      const lastDotIndex = stringValue.lastIndexOf('.');

      if (lastCommaIndex > lastDotIndex) {
        // Coma es decimal: remover puntos y reemplazar coma por punto
        stringValue = stringValue.replaceAll('.', '').replace(',', '.');
      } else {
        // Punto es decimal: remover comas
        stringValue = stringValue.replaceAll(',', '');
      }
    }

    const parsed = Number.parseFloat(stringValue);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Extrae las categorías de cada fondo desde la estructura fund > subBalances
   * Agrupa por categoría y luego por fondo dentro de cada categoría
   * @returns Array de categorías con sus fondos enriquecidos
   */
  private extractCategoriasFromFundos(): any[] {
    if (!this.accountBalance?.fund || !Array.isArray(this.accountBalance.fund)) {
      return [];
    }

    // Mapa para agrupar por categoría
    const categoriasMap = new Map<string, any>();

    // Iterar sobre los fondos
    this.accountBalance.fund.forEach((fondo: any) => {
      if (fondo.subBalances && Array.isArray(fondo.subBalances)) {
        fondo.subBalances.forEach((subBalance: any) => {
          if (subBalance.category) {
            // Si la categoría no existe, crearla
            if (!categoriasMap.has(subBalance.category) && Number(subBalance.clpBalance) > 0) {
              categoriasMap.set(subBalance.category, {
                category: subBalance.category,
                fondos: []
              });
            }

            let glosaSegunRegimen = this.util.glosaCodigoRegimen(subBalance.taxRegimeCode, subBalance.category, this.accountBalance.id);
            if (this.accountBalance.id === 'CCV' || this.accountBalance.id === 'CVC')
              glosaSegunRegimen = glosaSegunRegimen + ' - ' + subBalance.category;

            // Agregar el fondo a la categoría
            const categoriaObj = categoriasMap.get(subBalance.category);
            if (Number(subBalance.clpBalance) > 0) { // Solo agregar fondos con saldo positivo       
              categoriaObj.fondos.push({
                id: fondo.id,
                amount: fondo.amount,
                amountPercentage: fondo.amountPercentage,
                shareBalance: subBalance.shareBalance,
                clpBalance: this.util.currencyFormat(subBalance.clpBalance),
                clpBalanceRaw: subBalance.clpBalance, // Valor sin formato para el control
                taxRegimeCode: subBalance.taxRegimeCode,
                balanceState: subBalance.balanceState,
                shareValueDate: subBalance.shareValueDate,
                shareValue: subBalance.shareValue,
                glosaCodigoRegimen: glosaSegunRegimen
              });              
            }
          }
        });
      }
    });

    // Convertir el mapa a un array ordenado
    return Array.from(categoriasMap.values()).sort((a, b) =>
      a.category.localeCompare(b.category)
    );
  }

  /**
   * Obtener las categorías únicas desde los fondos
   * @returns Array de categorías únicas
   */
  getCategoriasTotales(): string[] {
    return this.fondosConCategorias.map(cat => cat.category);
  }

  // edit() eliminado, ya no se usa

  async save() {
    const registros: any[] = [];
    const tipoProducto = this.accountBalance?.id;

    if (!tipoProducto) {
      console.warn('No se pudo guardar: accountBalance.id no disponible');
      this.mode = 'summary';
      return;
    }

    if (!this.validateAllEditableAmounts()) {
      this.cdr.markForCheck();
      return;
    }

    // Iterar sobre todas las categorías
    this.categoriasDesglosadaPorRegimen.forEach((categoria) => {
      categoria.regimenes.forEach((regimen: any) => {
        // Iterar sobre los fondos de cada categoría
        regimen.fondos.forEach((fondo: any) => {
          const controlName = fondo.controlName;
          const maxAmount = this.getMaxAmountPesos(fondo);
          const minAmount = this.getMinAmountPesos(fondo);
          const shareBalance = this.getShareBalanceValue(fondo);
          const includeAll = this.isIncludeAll(controlName);
          let valorMontoSolicitado = maxAmount;
          let cuotaCalculada = 0;

          // CCV + ESTADO no es editable: se persiste el saldo original igualmente en sesión.
          if (!this.isCcvEstadoNoEditable(categoria.category)) {
            const rawMontoSolicitado = this.form.get(controlName)?.value;
            const parsedMonto = this.getClpBalanceValue(rawMontoSolicitado ?? 0);
            valorMontoSolicitado = Number.isNaN(parsedMonto)
              ? 0
              : Math.min(parsedMonto, maxAmount);

            if (this.esSaldoPolvoConIncluirTodo(includeAll, shareBalance, maxAmount, minAmount)) {
              valorMontoSolicitado = minAmount;
              cuotaCalculada = MIN_CUOTAS_DESTINO;
            }

            this.form.get(controlName)?.setValue(valorMontoSolicitado, { emitEvent: false });
          }

          // Construir el registro con la estructura especificada
          // Obtener shareValue de forma segura y calcular cuotas con decimales correctos
          const shareValueValido = this.getShareValue(fondo);
          if (cuotaCalculada === 0) {
            cuotaCalculada = shareValueValido > 0 ? valorMontoSolicitado / shareValueValido : 0;
          }

          const registro = {
            tipoProducto, // El id del producto (CAV, CCV, etc.)
            tipoFondo: fondo.id,
            tipoMoneda: 'P',
            valorMontoSolicitado: valorMontoSolicitado,
            categoria: categoria.category,
            codigoRegimenTributario: fondo.taxRegimeCode,
            cuota: fondo.shareBalance,
            glosaCodigoRegimen: this.util.glosaCodigoRegimen(fondo.taxRegimeCode, categoria.category, tipoProducto),
            glosaTipoCuenta: this.util.glosaTipoCuenta(categoria.category, fondo.taxRegimeCode, tipoProducto),
            cuotaSegunMontoIngresado: cuotaCalculada
          };

          registros.push(registro);
        });
      });
    });

    this.appendCcvEstadoDefaultRecords(registros, tipoProducto);

    const registrosStorage = await this.storageService.get(this.storageKey);
    const registrosExistentes = Array.isArray(registrosStorage) ? registrosStorage : [];

    // Mantiene registros de otros productos y reemplaza solo los del producto actual.
    const registrosOtrosProductos = registrosExistentes.filter(
      (item: any) => item?.tipoProducto !== tipoProducto
    );

    const registrosActualizados = [...registrosOtrosProductos, ...registros];
    await this.storageService.set(this.storageKey, registrosActualizados);

    const registrosEnMemoria = await this.storageService.get(this.storageKey);
    
    this.resumenSaldoInterno = Array.isArray(registrosEnMemoria) ? registrosEnMemoria : [];
    this.resumenAgrupadoPorTipoCuenta = this.buildResumenAgrupadoPorTipoCuenta();
    this.hasSavedAmounts = this.hasSavedDataForCurrentProduct();

    // Forzar change detection después de actualizar la sesión
    this.cdr.markForCheck();

    this.mode = 'summary'; // Solo cierra edición, el avance lo maneja el padre
    this.saved.emit(); // Emitir evento para que el padre avance a la siguiente pregunta
  }

  private appendCcvEstadoDefaultRecords(registros: any[], tipoProducto: string): void {
    if (tipoProducto !== 'CCV' && tipoProducto !== 'CVC') {
      return;
    }

    const categoriaEstado = this.fondosConCategorias.find(
      (categoria) => String(categoria?.category ?? '').toUpperCase() === 'ESTADO'
    );

    if (!categoriaEstado?.fondos?.length) {
      return;
    }

    const existingKeys = new Set(
      registros.map((item: any) => `${item.tipoFondo}_${item.categoria}_${item.codigoRegimenTributario}`)
    );

    categoriaEstado.fondos.forEach((fondo: any) => {
      const key = `${fondo.id}_ESTADO_${fondo.taxRegimeCode}`;
      if (existingKeys.has(key)) {
        return;
      }

      const maxAmount = this.getClpBalanceValue(fondo.clpBalanceRaw ?? fondo.clpBalance ?? 0);
      const valorMontoSolicitado = Number.isNaN(maxAmount) ? 0 : maxAmount;

      // Calcular cuotaSegunMontoIngresado también para ESTADO
      const shareValueValido = this.getShareValue(fondo);
      const cuotaCalculada = shareValueValido > 0 ? valorMontoSolicitado / shareValueValido : 0;

      registros.push({
        tipoProducto,
        tipoFondo: fondo.id,
        tipoMoneda: 'P',
        valorMontoSolicitado,
        categoria: 'ESTADO',
        codigoRegimenTributario: fondo.taxRegimeCode,
        cuota: fondo.shareBalance,
        glosaCodigoRegimen: this.util.glosaCodigoRegimen(fondo.taxRegimeCode, 'ESTADO', tipoProducto),
        glosaTipoCuenta: this.util.glosaTipoCuenta('ESTADO', fondo.taxRegimeCode, tipoProducto),
        cuotaSegunMontoIngresado: cuotaCalculada
      });
    });
  }

  selectCurrency(currency: string) {
    this.form.get(this.question.currencyControl)?.setValue(currency);
  }

  /**
   * TrackBy para mejorar rendimiento en *ngFor de categorías
   */
  trackByCategoriaIndex(index: number, categoria: any): string {
    return categoria.category;
  }

  trackByRegimenTaxCode(index: number, regimen: any): string {
    return `${regimen.taxRegimeCode}_${regimen.glosaCodigoRegimen}`;
  }

  /**
   * TrackBy para mejorar rendimiento en *ngFor de fondos
   */
  trackByFondoId(index: number, fondo: any): string {
    return fondo.controlName || `${fondo.id}_${fondo.taxRegimeCode}`;
  }

  /**
   * TrackBy para mejorar rendimiento en *ngFor de resumen
   */
  trackByRegistroIndex(index: number): number {
    return index;
  }

  trackByResumenTipoCuenta(index: number, grupo: any): string {
    return grupo.tipoCuenta;
  }

  trackByResumenRegistro(index: number, registro: any): string {
    return `${registro.tipoProducto}_${registro.glosaTipoCuenta}_${registro.tipoFondo}_${registro.codigoRegimenTributario}_${registro.categoria}_${index}`;
  }

  /**
   * Formatea el número de cuotas para mostrar en la tabla con 2 decimales
   */
  formatCuotasSegunMonto(cuota: any): string {
    const num = Number(cuota);
    if (Number.isNaN(num) || !Number.isFinite(num)) {
      return '0.00';
    }
    return num.toFixed(2);
  }

  goToVideoAtencion() {
    void this.modalService.openGenericModalMensajeSalida(TipoMensajeSalidaFlujoTramitePension.VideoAtencion);
  }

  goToEstimador() {
    void this.modalService.openGenericModalMensajeSalida(TipoMensajeSalidaFlujoTramitePension.Estimador);
  }
}